import { useEffect, useRef, useState } from 'react';

/**
 * useSpeechRecognition — the browser's built-in speech-to-text (Web Speech API).
 *
 * PROTOTYPE GRADE. Works in Chrome and Edge (and Safari); Firefox has none. In
 * Chrome the audio is transcribed by Google's servers, so before production this
 * should be swapped for a speech-to-text service the practice has a data
 * agreement with. The hook's shape (start / stop / interim / onFinal) is the seam.
 *
 * Dictation, not a voice command: a doctor pauses to think mid-case, so the
 * browser's own end-of-speech (about a second of quiet) is too eager. We listen
 * continuously and decide ourselves when the dictation is over:
 *   - silenceMs of quiet after the last word (default 3 s), or
 *   - the doctor taps the microphone again, or
 *   - maxMs in total (default 2 minutes).
 * Chrome also ends continuous sessions on its own now and then; when that
 * happens mid-dictation we reopen quietly and keep what was already heard.
 *
 * onFinal(text) fires once, with everything said.
 */
const FATAL = ['not-allowed', 'service-not-allowed', 'audio-capture', 'language-not-supported'];
const MAX_RESTARTS = 20;
const join = (...parts) => parts.map((p) => (p || '').trim()).filter(Boolean).join(' ');

const useSpeechRecognition = ({ lang = 'en-ZA', onFinal, silenceMs = 3000, maxMs = 120000 } = {}) => {
  const Recognition = typeof window !== 'undefined' ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState('');
  const recRef = useRef(null);
  const activeRef = useRef(false);      // the doctor still wants to be listened to
  const committedRef = useRef('');      // final text from earlier browser sessions
  const sessionRef = useRef('');        // final text from the current browser session
  const heardRef = useRef(false);
  const restartsRef = useRef(0);
  const silenceTimer = useRef(null);
  const maxTimer = useRef(null);
  const onFinalRef = useRef(onFinal);
  onFinalRef.current = onFinal;

  const clearTimers = () => {
    clearTimeout(silenceTimer.current);
    clearTimeout(maxTimer.current);
  };

  const finalize = () => {
    clearTimers();
    activeRef.current = false;
    setListening(false);
    setInterim('');
    const text = committedRef.current.trim();
    committedRef.current = '';
    sessionRef.current = '';
    if (text) onFinalRef.current?.(text);
  };

  // End the dictation: stop the browser, and onend hands over the text.
  const finish = () => {
    activeRef.current = false;
    clearTimers();
    if (recRef.current) recRef.current.stop();
    else finalize();
  };

  const armSilence = () => {
    clearTimeout(silenceTimer.current);
    silenceTimer.current = setTimeout(finish, silenceMs);
  };

  const open = () => {
    const rec = new Recognition();
    rec.lang = lang;
    rec.interimResults = true;
    rec.continuous = true;
    rec.maxAlternatives = 1;

    rec.onresult = (e) => {
      let finalText = '';
      let pending = '';
      for (let i = 0; i < e.results.length; i += 1) {
        const r = e.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else pending += r[0].transcript;
      }
      sessionRef.current = finalText;
      const shown = join(committedRef.current, finalText, pending);
      setInterim(shown);
      if (shown) {
        heardRef.current = true;
        armSilence();
      }
    };
    rec.onerror = (e) => {
      if (e.error === 'aborted') return;
      // Quiet before the first word ends it; quiet after words is our silence timer's job.
      if (e.error === 'no-speech' && heardRef.current) return;
      if (e.error === 'no-speech' || FATAL.includes(e.error)) activeRef.current = false;
      setError(
        e.error === 'not-allowed' || e.error === 'service-not-allowed'
          ? 'Microphone access is blocked. Allow it in the browser to speak.'
          : e.error === 'no-speech'
            ? 'I didn’t hear anything — tap the microphone and try again.'
            : `Speech recognition stopped (${e.error}).`
      );
    };
    rec.onend = () => {
      committedRef.current = join(committedRef.current, sessionRef.current);
      sessionRef.current = '';
      recRef.current = null;
      if (activeRef.current && restartsRef.current < MAX_RESTARTS) {
        restartsRef.current += 1;
        try {
          open();
          return;
        } catch {
          // fall through and hand over what was heard
        }
      }
      finalize();
    };

    recRef.current = rec;
    rec.start();
  };

  const start = () => {
    if (!Recognition || activeRef.current) return;
    activeRef.current = true;
    committedRef.current = '';
    sessionRef.current = '';
    heardRef.current = false;
    restartsRef.current = 0;
    setInterim('');
    setError('');
    try {
      open();
      setListening(true);
      maxTimer.current = setTimeout(finish, maxMs);
    } catch (e) {
      activeRef.current = false;
      recRef.current = null;
      setError(`Could not start the microphone (${e.message}).`);
    }
  };

  useEffect(() => () => {
    activeRef.current = false;
    clearTimers();
    recRef.current?.abort();
  }, []);

  return { supported: !!Recognition, listening, interim, error, start, stop: finish };
};

export default useSpeechRecognition;
